import { useState, useEffect } from "react";
import "./ResponseContent.css";
import { Question, QuestionType, QuestionComment, FormExtended } from "effno-core";
import { ResponseContentShort } from "./ResponseContentShort";
import { ResponseContentEnd } from "./ResponseContentEnd";
import { ResponseContentMultiple } from "./ResponseContentMultiple";
import { ResponseContentEmail } from "./ResponseContentEmail";
import { ResponseContentLong } from "./ResponseContentLong";
import { ResponseContentWebsite } from "./ResponseContentWebsite";
import { Comment } from "../comment/Comment";
import toast, { Toaster } from "react-hot-toast";
import { ResponseContentDate } from "./ResponseContentDate";
import { ResponseContentNumber } from "./ResponseContentNumber";
import { ResponseContentPhone } from "./ResponseContentPhone";
import { ResponseContentContact } from "./ResponseContentContact";
import { ResponseContentNPS } from "./ResponseContentNPS";
import { ResponseContentScore } from "./ResponseContentScore";
import { ResponseContentRating } from "./ResponseContentRating";
import { PublicUserDetailsModal } from "./PublicUserDetailsModal";
import { CommentAvatar } from "../comment/CommentAvatar";
import { FormProgressBar } from "../form-progress-bar/FormProgressBar";
import { FormClosedReason } from "../form-collect/FormCollect"; 
import { ResponseContentSection } from "./ResponseContentSection";
import { ICONS } from "../shared/media";
import { FormThemeSettings } from "../question-design/QuestionDesign";
import { lightenColor } from "../shared/uiHelpers";
import { apiPing } from "../../services/PingService";
import { useSelector } from "react-redux";
import { selectToken, selectUser } from "../../redux/features/appData";
import { toastSuccess } from "../../config";
import { ArrowRight } from "lucide-react";
import { ResponseContentClosed } from "./ResponseContentClosed";

export const NEXT_BUTTON = "Next";

export type PublicDetails = {
    publicName: string;
    publicEmail: string;
}

type ResponseContentProps = {
    isPreview: boolean;
    isClosed: boolean;
    closedReason: FormClosedReason;
    commentMode: boolean;
    isMobileLayout: boolean;
    isForcedEnd: boolean;
    form: FormExtended | undefined;
    question: Question | undefined;
    responseValues: string;
    progressPercentage?: number;
    onNext?: () => void;
    onPrev?: () => void;
    onSubmit: (responseValues: string) => void;
    onNewComment?: (comment: QuestionComment) => void;
    onCompletedComment?: (commentId: string) => void;
}

export const ResponseContent = ({
    isPreview,
    isClosed = false,
    closedReason,
    commentMode,
    isMobileLayout,
    isForcedEnd,
    form,
    question,
    responseValues,
    progressPercentage,
    onNext,
    onPrev, 
    onSubmit,
    onNewComment,
    onCompletedComment,
} : ResponseContentProps) => {

    // const uiStates = useSelector(selectUIStates);
    // const editingForm = useSelector(selectEditingForm);
    // we don't use redux for anything but we do check to see if the user has a valid
    // login token and user account
    const storeUser = useSelector(selectUser);
    const storeToken = useSelector(selectToken);

    const [selectedComment, setSelectedComment] = useState<QuestionComment | undefined>(undefined);
    const [commentXP, setCommentXP] = useState<number | undefined>(undefined);
    const [commentYP, setCommentYP] = useState<number | undefined>(undefined);
    const [viewingComment, setViewingComment] = useState(false);
    const [hoveringCommentId, setHoveringCommentId] = useState<string | undefined>(undefined);
    const [commentTimeoutId, setCommentTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);

    const [viewingFormThemeSettings, setViewingFormThemeSettings] = 
        useState<FormThemeSettings | undefined>(undefined);

    const [collectPulicUserDetails, setCollectPublicUserDetails] = useState(false);
    const [publicDetails, setPublicDetails] = useState<PublicDetails | undefined>(undefined);

    useEffect(() => {
        // grab these in case they are there and we need them
        const publicName = localStorage.getItem('publicName');
        const publicEmail = localStorage.getItem('publicEmail');

        if (publicName && publicEmail) {
            setPublicDetails({
                publicName,
                publicEmail
            });
        }
    }, []);

    useEffect(() => {
        if (storeUser) {
            localStorage.removeItem('publicName');
            localStorage.removeItem('publicEmail');
            setPublicDetails(undefined);
        }
    }, [storeUser]);

    useEffect(() => {
        if (form?.formSettings) {
            setViewingFormThemeSettings(JSON.parse(form?.formTheme?.settings || "{}") as FormThemeSettings);
        }
    }, [form]);
    
    useEffect(() => {

    }, [question, responseValues, form]);


    const isAuthorized = async () => {

        const result = await apiPing();
        console.log("[ResponseContent/isAuthorized] result: ", result);

        if (!result) {
            return false;
        }

        if (!storeToken || !storeUser) {
            return false;
        }

        return true;
    }

    const handleCreateComment = async (e: any) => {
        console.log("[handleCreateComment] isForcedEnd: ", isForcedEnd);
        console.log("[handleCreateComment] question: ", question);
        
        if (!question) {
            toast("You can't comment on this view", toastSuccess);
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();

        // Calculate the relative X and Y as percentages
        // setCommentX(e.clientX - rect.left);
        // setCommentY(e.clientY - rect.top);

        const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
        const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
        // console.log(`[ResponseContent/handleComment] Relative X: ${xPercent.toFixed(2)}%, Relative Y: ${yPercent.toFixed(2)}%`);

        setCommentXP(xPercent);
        setCommentYP(yPercent);

        // figure out if we have a user or not, if not, get their details
        const authed =  await isAuthorized();
        
        console.log("[ResponseContent] authed: ", authed);
        console.log("[ResponseContent] publicDetails: ", publicDetails);

        if (!authed && !publicDetails) {
            setCollectPublicUserDetails(true);
            return;
        }     

        setViewingComment(true);
        setSelectedComment(undefined);
    }

    const handleOpenComment = (comment: QuestionComment) => {
        // alert("Opening comment");

        // console.log("[ResponseContent] handleOpenComment: ", comment);

        setViewingComment(true);
        setSelectedComment({...comment});
        setCommentXP(comment.xpercent);
        setCommentYP(comment.ypercent);
    }

    const handleCommentMouseEnter = (comment: QuestionComment) => {
        setHoveringCommentId(comment.id);

        const timeoutId = setTimeout(() => {
            setHoveringCommentId(undefined);
            handleOpenComment(comment);
        }, 100);

        setCommentTimeoutId(timeoutId);
    }

    const handleCommentMouseLeave = () => {
        if (commentTimeoutId) {
            clearTimeout(commentTimeoutId);
        }

        setHoveringCommentId(undefined);
    }

    const handlePublicUserDetails = (publicName: string, publicEmail: string) => {
        setPublicDetails({
            publicName,
            publicEmail
        });

        // save them for later
        localStorage.setItem('publicName', publicName);
        localStorage.setItem('publicEmail', publicEmail);
      
        setCollectPublicUserDetails(false);

        // now open the comment
        setViewingComment(true);
        setSelectedComment(undefined);
    }

    const parentComments = question ? question.comments.filter((comment) => !comment.parentQuestionCommentId) : [];

    const brightness = viewingFormThemeSettings?.imageBrightness || 1.0;

    // const formThemeStyle = { 
    //     "--ftBackgroundColor": viewingFormThemeSettings?.backgroundColor || "var(--background-color-primary)",
    //     "--ftQuestionColor": viewingFormThemeSettings?.questionColor || "var(--text-color-primary)",
    //     "--ftAnswerColor": viewingFormThemeSettings?.answerColor || "var(--text-color-primary)",
    //     "--ftAnswerBackgroundColor": viewingFormThemeSettings?.answerColor ?
    //         lightenColor(viewingFormThemeSettings?.answerColor, 60) :
    //         "var(--background-color-tertiary)",
    //     "--ftFont": viewingFormThemeSettings?.font || "var(--font-primary)",
    //     "--ftButtonColor": viewingFormThemeSettings?.buttonColor || "var(--button-color-primary)",
    //     "--ftButtonTextColor": viewingFormThemeSettings?.buttonTextColor || "var(--button-text-primary)",
    //     "--ftBackgroundImage": `url(${viewingFormThemeSettings?.backgroundImage || ""})`, 
    //     "--ftImageBrightness": `brightness(${brightness})`,
    //     "--ftImageOpacity": `${brightness}`,
    // } as React.CSSProperties

    const formThemeStyle = { 
        "--ftBackgroundColor": viewingFormThemeSettings?.backgroundColor || "var(--default-ftBackgroundColor)",
        "--ftQuestionColor": viewingFormThemeSettings?.questionColor || "var(--default-ftQuestionColor)",
        "--ftAnswerColor": viewingFormThemeSettings?.answerColor || "var(--default-ftAnswerColor)",
        "--ftAnswerBackgroundColor": viewingFormThemeSettings?.answerColor ?
            lightenColor(viewingFormThemeSettings?.answerColor, 80) :
            "var(--default-ftAnswerBackgroundColor)",
        "--ftFont": viewingFormThemeSettings?.font || "var(--default-ftFont)",
        "--ftButtonColor": viewingFormThemeSettings?.buttonColor || "var(--default-ftButtonColor)",
        "--ftButtonTextColor": viewingFormThemeSettings?.buttonTextColor || "var(--default-ftButtonTextColor)",
        "--ftBackgroundImage": `url(${viewingFormThemeSettings?.backgroundImage || ""})`, 
        "--ftImageBrightness": `brightness(${brightness})`,
        "--ftImageOpacity": `${brightness}`,
    } as React.CSSProperties

    const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : [];
    const questionNumber = question ? questionOrder.indexOf(question.id) + 1 : 0;

    const nonEndQuestions = form?.questions.filter((q) => q.type !== QuestionType.END).length
    const lastQuestion = questionNumber === nonEndQuestions;

    const redirectURL = (form?.formSettings && form?.formSettings.redirectOnCompletion) ? 
        form?.formSettings.redirectURL : undefined;

    const showClosed = !isPreview && isClosed;
    const showQuestionNumber = !isClosed && !isForcedEnd &&
        (form?.formSettings && form?.formSettings.questionNumbers && 
            question?.type !== QuestionType.END && question?.type !== QuestionType.SECTION);
   
    // console.log("[ResponseContent] question: ", question);
    // console.log("[ResponseContent] nonEndQuestions: ", nonEndQuestions);
    // console.log("[ResponseContent] questionNumber: ", questionNumber);
    // console.log("[ResponseContent] lastQuestion: ", lastQuestion);
    console.log("[ResponseContent] showQuestionNumber: ", showQuestionNumber);
    
    return (
        <div 
            className={isMobileLayout ? "response-content-noimage" : "response-content"}
            style={{
                    ...formThemeStyle, 
                    backgroundColor: isMobileLayout ? 
                        "var(--default-ftBackgroundColor" : "var(--ftBackgroundColor)"
            }}
        >
            <div 
                className="response-content-wrapper"
                style={(isMobileLayout && !isPreview) ? {padding: "0px", margin: "0px"} : {}}
            >
                {progressPercentage && 
                    <FormProgressBar 
                        progressPercentage={progressPercentage}
                        isMobileLayout={isMobileLayout}
                        buttonColor={viewingFormThemeSettings?.buttonColor}
                    />
                }
                
                <div 
                    className="response-content-wrapper-comments"
                    style={(isPreview && commentMode) ? {cursor: "crosshair !important"} : {}}
                >
                    {commentMode && !viewingComment &&
                        <div 
                            className="form-content-floating-comments"
                            onClickCapture={(e: any) => {
                                if (!viewingComment) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCreateComment(e);
                                }
                                return;
                                
                            }}
                        >
                            {}
                        </div>
                    }

                    <div 
                        className="response-content-wrapper-comments-inner"
                        // style={(isPreview && commentMode) ? 
                        //     {cursor: "crosshair !important"} 
                        //     // {cursor: `url("../public/icons/light-mode/close.svg")`}
                        //     : 
                        //     {}}
                        // onClickCapture={commentMode ? (
                        //     (e: any) => {
                        //         console.log("[ResponseContent] Clicked on response-content-wrapper-comments-inner, commendMode: ", commentMode);
                        //         if (commentMode) {
                        //             if (!viewingComment) {
                        //                 e.preventDefault();
                        //                 e.stopPropagation();
                        //                 handleCreateComment(e);
                        //             }
                        //             return;
                        //         }
                        //     }
                        // ) : undefined}
                        // onClickCapture={(e: any) => {
                        //         console.log("[ResponseContent] Clicked on response-content-wrapper-comments-inner, commendMode: ", commentMode);
                        //         if (commentMode) {
                        //             if (!viewingComment) {
                        //                 e.preventDefault();
                        //                 e.stopPropagation();
                        //                 handleCreateComment(e);
                        //             }
                        //             return;
                        //         }
                        //     }
                        // }
                    >
                        {/* <div 
                            className={isMobileLayout ? "response-content-mobile" : "response-content-laptop"}
                            style={(question && question.type === QuestionType.CONTACT) ? 
                                {padding: "5% 15%"} : {}
                            }
                        > */}

                        <div 
                            className={isMobileLayout ? 
                                (isPreview ? "preview-content-mobile" : "response-content-mobile") : "response-content-laptop"}
                            
                        >
                            <div 
                                className="response-content-container"
                                style={{...(question?.type === QuestionType.CONTACT || question?.type === QuestionType.MULTIPLE) ?
                                                {overflowY: "auto", scrollbarWidth: "none"} : {},
                                        ...(question?.type === QuestionType.CONTACT || question?.type === QuestionType.MULTIPLE) ? 
                                                isMobileLayout ? {padding: "5% 5% 15% 5%", marginBottom: "25%"} : {padding: "0% 15%", marginBlock: "0px"} : 
                                                isMobileLayout ? {padding: "5%", alignItems: "center"} : {padding: "15%", alignItems: "center"}
                                    }}
                            >
                                <div 
                                    className="form-content-container-inside"
                                    // style={isMobileLayout ? {marginBlock: "auto"} : {}}
                                >
                                    
                                    <>
                                        {showQuestionNumber &&
                                            <div 
                                                className={isMobileLayout ? 
                                                    "form-content-pointer-mobile" : 
                                                    "form-content-pointer"
                                                }
                                                style={{paddingTop: isMobileLayout ? "2px" : "4px"}}
                                            >
                                                <div 
                                                    className="form-content-pointer-arrow"
                                                    style={{paddingTop: isMobileLayout ? "6px" : "6px"}}
                                                >
                                                    {form?.formSettings?.questionNumbers &&
                                                        <div className="form-content-pointer-arrow-container">
                                                            {questionNumber}
                                                            <ArrowRight size={20} color="var(--ftQuestionColor)" />

                                                            {/* <img src={"/icons/light-mode/" + ICONS.ARROW} /> */}
                                                        </div>
                                                    }
                                                </div> 
                                            </div>
                                        }
                                        {question && question.type !== QuestionType.END && question.type !== QuestionType.SECTION &&
                                            <div 
                                            className="form-content-required"
                                            style={{paddingTop: isMobileLayout ? "7px" : "10px"}}
                                        >
                                            {question?.required && <span>*</span>}
                                        </div>
                                        }
                                       
                                    </>
                                    
                        
                                    {!isForcedEnd && !showClosed && question && (
                                        () => {
                                            switch(question.type) {
                                                case QuestionType.SHORT:
                                                    return (
                                                        <ResponseContentShort 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.LONG:
                                                    return (
                                                        <ResponseContentLong 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.MULTIPLE:
                                                    return (
                                                        <ResponseContentMultiple 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )      
                                                case QuestionType.DATE:
                                                    return (
                                                        <ResponseContentDate
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.EMAIL:
                                                    return (
                                                        <ResponseContentEmail 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )  
                                                case QuestionType.FILE_UPLOAD:
                                                    return (
                                                        <ResponseContentShort 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.WEBSITE:
                                                    return (
                                                        <ResponseContentWebsite 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.NUMBER:
                                                    return (
                                                        <ResponseContentNumber 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.PHONE:
                                                    return (
                                                        <ResponseContentPhone 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.CONTACT:
                                                    return (
                                                        <ResponseContentContact 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.NPS:
                                                    return (
                                                        <ResponseContentNPS 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.SCORE:
                                                    return (
                                                        <ResponseContentScore 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.RATING:
                                                    return (
                                                        <ResponseContentRating 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.SECTION:
                                                    return (
                                                        <ResponseContentSection 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={lastQuestion}
                                                            commentMode={commentMode}
                                                            onNext={onNext}
                                                            onPrev={onPrev}
                                                            onSubmit={onSubmit}
                                                        />
                                                    )
                                                case QuestionType.END:
                                                    return (
                                                        <ResponseContentEnd 
                                                            question={question}
                                                            responseValues={responseValues}
                                                            isPreview={isPreview}
                                                            isMobileLayout={isMobileLayout}
                                                            isLastQuestion={false}
                                                            commentMode={commentMode}
                                                            onPrev={onPrev}
                                                            redirectURL={redirectURL}
                                                            // onNext={onNext}
                                                            // onPrev={onPrev}
                                                            // onSubmit={onSubmit}
                                                        />
                                                    )
                                                default:
                                                    return (
                                                        <div>
                                                            
                                                        </div>
                                                    );
                                            }
                                        }
                                        )()
                                    }

                                    {isForcedEnd && !showClosed &&
                                        <ResponseContentEnd 
                                            question={undefined}
                                            responseValues={responseValues}
                                            isPreview={isPreview}
                                            isMobileLayout={isMobileLayout}
                                            isLastQuestion={false}
                                            commentMode={commentMode}
                                            onPrev={onPrev}
                                            redirectURL={redirectURL}
                                        />
                                    }

                                    {showClosed &&
                                        <div className="response-content-closed">
                                            <ResponseContentClosed closedReason={closedReason} />
                                        </div>
                                    }

                                    {/* {isPreview && commentMode && 
                                        <div className="response-content-comment-blocker">

                                        </div>
                                    } */}
                                </div>
                            </div>
                        </div>
                    </div>

                    {question && isPreview && commentMode && viewingComment && commentXP !== undefined 
                        && commentYP !== undefined &&
                        <div 
                            className="response-content-comment"
                            style={{top: `${commentYP}%`, left: `${commentXP}%`}}
                        >
                            <Comment 
                                user={storeUser}
                                form={form}
                                publicDetails={publicDetails}
                                comment={selectedComment}
                                comments={question.comments}
                                questionId={question.id}
                                xp={commentXP}
                                yp={commentYP}
                                onNewComment={onNewComment}
                                onCompletedComment={onCompletedComment}
                                onClose={() => setViewingComment(false)}
                            />
                        </div>
                    }

                    {question && isPreview && commentMode && parentComments.length > 0 
                        && parentComments.map((comment) => {
                            const publicDetails : PublicDetails | undefined = 
                                (comment.publicName && comment.publicEmail) ? {
                                    publicName: comment.publicName,
                                    publicEmail: comment.publicEmail
                                } : undefined;

                        return (
                            <div 
                                key={"comment-" + comment.id}
                                className="response-content-comment-marker"
                                style={{top: `${comment.ypercent}%`, left: `${comment.xpercent}%`}}
                                onClick={(e: any)=> {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleOpenComment(comment)
                                }}
                                onMouseEnter={()=>handleCommentMouseEnter(comment)}
                                onMouseLeave={handleCommentMouseLeave}
                            >
                                <CommentAvatar publicDetails={publicDetails} user={comment.user}/>
                            </div>
                        )}
                    )}

                    {collectPulicUserDetails &&
                        <PublicUserDetailsModal 
                            isOpen={collectPulicUserDetails}
                            onClose={() => setCollectPublicUserDetails(false)}
                            onSubmit={handlePublicUserDetails}
                        />
                    
                    }
                </div>
            </div>  

            <Toaster />

        </div>
    )
}